import { useState, useMemo, useCallback, useEffect } from "react";
import {
  Table,
  Checkbox,
  Button,
  Tooltip,
  Popover,
  Pagination,
} from "@narmi/design_system";
import { useLocalization } from "@fluent/react";
import { modules, entities, getInForm } from "byzantine";
import {
  BulkPaymentRowType,
  getBulkPaymentStartDate,
  useCreateBulkPaymentRowActions,
} from "byzantine/src/dbbl/businessLogic/entities/bulkPayments";
import { useInstitutionSettings } from "../../../contexts/InstitutionSettingsContext";
import { useUserFeatures } from "../../../contexts/UserFeaturesContext";
import { RecipientDrawer, SECTION_NAMES } from "../../../RecipientDrawer";
import { PostingDialog } from "../";
import { useTemplateContext } from "../templates/TemplateContext";
import styles from "./BulkPaymentTable.module.scss";
import { BulkPaymentRow, ActionBar } from "./";
import useBulkTableState from "./useBulkTableState";
import useRecipientDrawerState from "./useRecipientDrawerState";
import UnsavedRecipientDrawer from "./UnsavedRecipientDrawer";

export const getRecipientById = (
  recipients: API.Recipient[],
  recipientId: API.RecipientId
) => recipients.find((r) => r.id === recipientId);

type SortOption = "fileOrder" | "error" | "speed";

const SORT_LABEL_KEYS: Record<SortOption, string> = {
  fileOrder: "bulk-sort-file-order",
  error: "bulk-sort-error",
  speed: "bulk-sort-speed",
};

const hasRowError = (form: any, index: number): boolean =>
  form.submitCount > 0 &&
  Boolean(
    getInForm(form.errors, `payments.${index}.recipient`) ||
      getInForm(form.errors, `payments.${index}.amount`) ||
      getInForm(form.errors, `payments.${index}.scheduledDate`)
  );

const BulkPaymentTable = ({
  isImportMode = false,
}: {
  isImportMode?: boolean;
}) => {
  // external hooks and context
  const { l10n } = useLocalization();
  const features = useUserFeatures();
  const { ach_next_cutoff: achNextCutoff } = useInstitutionSettings();
  const {
    expedited_payments: expeditedPaymentsFeature,
    fednow_send: fedNowFeature,
    ach_payments_collect: achPaymentsCollectFeature,
  } = features;
  const {
    isTemplateMode,
    allowExpedite,
    excludedRows,
    toggleRowInclusion,
    isPaymentFieldEditable,
  } = useTemplateContext();
  const form = entities.bulkPayments.BulkPaymentForm.useForm();
  const rowActions = useCreateBulkPaymentRowActions();
  // if user doesn't have manage recipient permissions, just show them recipients they can only send ach payments to
  const recipients = modules.achPayments.usePermissionedRecipients();
  const { send: refetchRecipients } =
    entities.recipients.useFetchRecipients();
  const recipientDrawer = useRecipientDrawerState(recipients, form);

  // Refetch recipients on mount in import mode so newly un-hidden
  // recipients from the RecipientReview screen appear in the store.
  useEffect(() => {
    if (isImportMode) {
      refetchRecipients();
    }
  }, []);

  // Unsaved recipient drawer state
  const [unsavedDrawerOpen, setUnsavedDrawerOpen] = useState(false);
  const [unsavedDrawerRecipient, setUnsavedDrawerRecipient] =
    useState<API.Recipient | null>(null);
  const [unsavedDrawerRowIndex, setUnsavedDrawerRowIndex] = useState<
    number | null
  >(null);

  const handleClickUnsavedRecipient = useCallback(
    (recipient: API.Recipient, rowIndex: number) => {
      setUnsavedDrawerRecipient(recipient);
      setUnsavedDrawerRowIndex(rowIndex);
      setUnsavedDrawerOpen(true);
    },
    []
  );

  const handleUnsavedRecipientUpdate = useCallback(
    (updatedRecipient: API.Recipient) => {
      if (unsavedDrawerRowIndex !== null) {
        form.setFieldValue(
          `payments.${unsavedDrawerRowIndex}.recipient`,
          updatedRecipient
        );
      }
    },
    [unsavedDrawerRowIndex, form]
  );

  const handleCloseUnsavedDrawer = useCallback(() => {
    setUnsavedDrawerOpen(false);
    setUnsavedDrawerRecipient(null);
    setUnsavedDrawerRowIndex(null);
  }, []);

  const {
    checkboxHeaderSelected,
    handleToggleCheckboxHeader,
    addRow,
    selectedRows,
    toggleRowSelection,
    handleClearRowsSelection,
    handleDuplicateRow,
    handleRemoveRows,
    calculateTotalAmount,
    postingDialogIsOpen,
    togglePostingDialog,
    eligibilityCacheRef,
  } = useBulkTableState(rowActions);

  // derived values
  const minDate = getBulkPaymentStartDate(
    expeditedPaymentsFeature,
    achNextCutoff,
    fedNowFeature
  );
  const { values } = form;

  // sort state for import mode
  const [sortBy, setSortBy] = useState<SortOption>("fileOrder");
  const [sortPopoverOpen, setSortPopoverOpen] = useState(false);

  const sortedPayments = useMemo(() => {
    const indexed = values.payments.map(
      (payment: BulkPaymentRowType, i: number) => ({
        payment,
        originalIndex: i,
      })
    );
    if (!isImportMode || sortBy === "fileOrder") return indexed;

    return [...indexed].sort((a, b) => {
      let diff = 0;
      if (sortBy === "error") {
        const aErr = hasRowError(form, a.originalIndex) ? 0 : 1;
        const bErr = hasRowError(form, b.originalIndex) ? 0 : 1;
        diff = aErr - bErr;
      } else {
        // sortBy === "speed"
        const speedRank = (
          s?: BulkPaymentRowType["speedStatus"]
        ): number => {
          if (s === "instant") return 0;
          if (s === "sameDay") return 1;
          if (s === "standard") return 2;
          return 3; // empty / unknown
        };
        diff =
          speedRank(a.payment.speedStatus) -
          speedRank(b.payment.speedStatus);
      }
      // tie-breaker: preserve file order for deterministic sorting
      return diff !== 0 ? diff : a.originalIndex - b.originalIndex;
    });
  }, [values.payments, sortBy, isImportMode, form]);

  const PAGE_SIZE = 20;
  const [currentPage, setCurrentPage] = useState(1);
  const totalPages = Math.ceil(sortedPayments.length / PAGE_SIZE);

  // Reset page when sort changes
  useEffect(() => {
    setCurrentPage(1);
  }, [sortBy]);

  // Clamp to last page if current page is out of bounds (e.g., rows removed)
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const displayedPayments = sortedPayments.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  // speed column shows whenever expedited_payments feature is enabled
  // in template mode with max_expedite=0, rows will display "Sending standard".
  const showSpeedColumn = expeditedPaymentsFeature;

  const COL_VISIBILITY: React.ComponentProps<typeof Table>["colVisibility"] = [
    ...(isImportMode ? ["*" as const] : []), // row number
    ...(showSpeedColumn
      ? [
          "*" as const, // include toggle / checkbox
          "*" as const, // date
          "*" as const, // recipient
          "*" as const, // action
          "*" as const, // speed
          "*" as const, // description
          "*" as const, // amount
        ]
      : [
          "*" as const, // include toggle / checkbox
          "*" as const, // date
          "*" as const, // recipient
          "*" as const, // action
          "*" as const, // description
          "*" as const, // amount
        ]),
  ];

  const toggleColWidth = isTemplateMode ? "100px" : "50px";
  const toggleColWidthNoSpeed = isTemplateMode ? "110px" : "60px";

  const rowNumCol = isImportMode ? "max-content " : "";

  // Use 1fr for the description column so the table fills available space
  // without causing horizontal scroll when the row number column is present
  const COL_LAYOUT: React.ComponentProps<typeof Table>["colLayout"] =
    showSpeedColumn
      ? {
          s: `${rowNumCol}${toggleColWidth} 155px 255px 140px 195px 1fr 205px`,
          m: `${rowNumCol}${toggleColWidth} 155px 255px 140px 195px 1fr 205px`,
          l: `${rowNumCol}${toggleColWidth} 155px 255px 140px 195px 1fr 205px`,
        }
      : {
          s: `${rowNumCol}${toggleColWidthNoSpeed} 185px 325px 145px 1fr 220px`,
          m: `${rowNumCol}${toggleColWidthNoSpeed} 185px 325px 145px 1fr 220px`,
          l: `${rowNumCol}${toggleColWidthNoSpeed} 185px 325px 145px 1fr 220px`,
        };

  return (
    <div>
      <div className={styles.postingTextContainer}>
        {values.groupPosting
          ? l10n.getString("posting-dialog-cta-combined")
          : l10n.getString("posting-dialog-cta-single")}
        {!isTemplateMode && (
          <span className="margin--left--xs">
            <Button
              kind="plain"
              onClick={togglePostingDialog}
              testId="open-posting-dialog"
            >
              {l10n.getString("button-edit")}
            </Button>
          </span>
        )}
      </div>
      <div className={styles.headingContainer}>
        <h4 className="fontSize--l nds-sans">
          {l10n.getString("bulk-table-header")}
        </h4>
        {isImportMode && (
          <span className={styles.badge}>{values.payments.length}</span>
        )}
        {isImportMode ? (
          <span className={styles.sortDropdown}>
            <span className={styles.sortButton}>
              <Popover
                content={
                  <div className={styles.sortMenu} role="menu">
                    {(
                      [
                        "error",
                        "fileOrder",
                        ...(showSpeedColumn
                          ? ["speed" as SortOption]
                          : []),
                      ] as SortOption[]
                    ).map((option) => (
                        <button
                          key={option}
                          type="button"
                          className={styles.sortMenuItem}
                          role="menuitemradio"
                          aria-checked={sortBy === option}
                          onClick={() => {
                            setSortBy(option);
                            setSortPopoverOpen(false);
                          }}
                        >
                          <span>{l10n.getString(SORT_LABEL_KEYS[option])}</span>
                          {sortBy === option && (
                            <span className="narmi-icon-check" />
                          )}
                        </button>
                      )
                    )}
                  </div>
                }
                isOpen={sortPopoverOpen}
                onUserDismiss={() => setSortPopoverOpen(false)}
              >
                <button
                  type="button"
                  className={styles.sortTrigger}
                  aria-haspopup="menu"
                  aria-expanded={sortPopoverOpen}
                  onClick={() => setSortPopoverOpen(!sortPopoverOpen)}
                >
                  {l10n.getString("bulk-sort-by-label", {
                    sortOption: l10n.getString(SORT_LABEL_KEYS[sortBy]),
                  })}
                  <span className="narmi-icon-chevron-down" />
                </button>
              </Popover>
            </span>
            {!isTemplateMode && (
              <span className={styles.addButton}>
                <Button
                  startIcon="plus"
                  label={l10n.getString("label-add-payment")}
                  testId="bulk-add-payment-button-top"
                  kind="secondary"
                  size="s"
                  onClick={addRow}
                />
              </span>
            )}
          </span>
        ) : (
          !isTemplateMode && (
            <span className={styles.addPaymentButtonTop}>
              <Button
                startIcon="plus"
                label={l10n.getString("label-add-payment")}
                testId="bulk-add-payment-button-top"
                kind="secondary"
                size="s"
                onClick={addRow}
              />
            </span>
          )
        )}
      </div>
      <Table
        kind="editable"
        colVisibility={COL_VISIBILITY}
        colLayout={COL_LAYOUT}
      >
        <Table.Header>
          <Table.Row>
            {isImportMode && <Table.HeaderCell>#</Table.HeaderCell>}
            <Table.HeaderCell>
              {isTemplateMode ? (
                l10n.getString("label-include")
              ) : (
                <Checkbox
                  onChange={handleToggleCheckboxHeader}
                  checked={checkboxHeaderSelected}
                />
              )}
            </Table.HeaderCell>
            <Table.HeaderCell>
              <div className={styles.headerWithTooltip}>
                <span className={styles.headerWithTooltipLabel}>
                  {l10n.getString("label-date")}
                </span>
                <Tooltip
                  maxWidth="300px"
                  text={l10n.getString("bulk-date-tooltip-copy")}
                  wrapperDisplay="flex"
                >
                  <span className="narmi-icon-info"></span>
                </Tooltip>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell>
              {achPaymentsCollectFeature
                ? l10n.getString("label-to-from")
                : l10n.getString("ach-payment-recipient-title")}
            </Table.HeaderCell>
            <Table.HeaderCell>
              {l10n.getString("label-action")}
            </Table.HeaderCell>
            {showSpeedColumn && (
              <Table.HeaderCell>
                <div className={styles.headerWithTooltip}>
                  <span className={styles.headerWithTooltipLabel}>
                    {l10n.getString("speed-label")}
                  </span>
                  <Tooltip
                    maxWidth="300px"
                    text={l10n.getString("speed-tooltip-copy")}
                    wrapperDisplay="flex"
                  >
                    <span className="narmi-icon-info"></span>
                  </Tooltip>
                </div>
              </Table.HeaderCell>
            )}
            <Table.HeaderCell>
              <div className={styles.headerWithTooltip}>
                <span className={styles.headerWithTooltipLabel}>
                  {l10n.getString("label-description-optional")}
                </span>
                <Tooltip
                  maxWidth="300px"
                  side="left"
                  text={l10n.getString("bulk-description-tooltip-copy")}
                  wrapperDisplay="flex"
                >
                  <span className="narmi-icon-info"></span>
                </Tooltip>
              </div>
            </Table.HeaderCell>
            <Table.HeaderCell textAlign={"right" as any}>
              {l10n.getString("label-amount")}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {displayedPayments.map(
            (
              {
                payment,
                originalIndex,
              }: {
                payment: BulkPaymentRowType;
                originalIndex: number;
              },
              displayIndex: number
            ) => (
              <BulkPaymentRow
                key={`bulk-payment-row__${originalIndex}`}
                payment={payment}
                index={originalIndex}
                rowNumber={
                  isImportMode
                    ? (currentPage - 1) * PAGE_SIZE +
                      displayIndex +
                      1
                    : undefined
                }
                form={form}
                recipients={recipients}
                selectedRows={selectedRows}
                toggleRowSelection={toggleRowSelection}
                handleClickEditRecipient={
                  recipientDrawer.handleClickEditRecipient
                }
                handleClickAddRecipient={
                  recipientDrawer.handleClickAddRecipient
                }
                handleClickUnsavedRecipient={handleClickUnsavedRecipient}
                minDate={minDate}
                setCustomAlertCopy={recipientDrawer.setCustomAlertCopy}
                eligibilityCache={eligibilityCacheRef.current}
                isTemplateMode={isTemplateMode}
                isExcluded={excludedRows.has(originalIndex)}
                onToggleInclude={() => toggleRowInclusion(originalIndex)}
                isFieldEditable={(field) =>
                  isPaymentFieldEditable(originalIndex, field)
                }
                allowExpedite={allowExpedite}
              />
            )
          )}
        </Table.Body>

        {!isTemplateMode && (
          <div className={styles.addPaymentButton}>
            <Button
              startIcon="plus"
              label={l10n.getString("label-add-payment")}
              testId="bulk-add-payment-button"
              kind="plain"
              onClick={addRow}
            />
          </div>
        )}
      </Table>

      {totalPages > 1 && (
        <div className={styles.paginationWrapper}>
          <Pagination
            totalPages={totalPages}
            selectedPage={currentPage}
            onPageChange={(page: number) => setCurrentPage(page)}
          />
        </div>
      )}

      {!isTemplateMode && (
        <ActionBar
          selectedRows={selectedRows}
          handleClearRowsSelection={handleClearRowsSelection}
          handleDuplicateRow={handleDuplicateRow}
          removeRows={handleRemoveRows}
          totalAmount={calculateTotalAmount(selectedRows, form.values.payments)}
        />
      )}

      <RecipientDrawer
        recipientId={recipientDrawer.selectedRecipientId}
        handleClose={recipientDrawer.handleCloseDrawer}
        isOpen={recipientDrawer.isRecipientDrawerOpen}
        fromACHFlow={true}
        drawerType={recipientDrawer.typeOfRecipientDrawer}
        rowIndex={recipientDrawer.selectedRowIndex}
        customAlertCopy={recipientDrawer.customAlertCopy}
        defaultSectionToOpen={
          recipientDrawer.customAlertCopy ? SECTION_NAMES.ach : undefined
        }
      />

      <UnsavedRecipientDrawer
        isOpen={unsavedDrawerOpen}
        onClose={handleCloseUnsavedDrawer}
        recipient={unsavedDrawerRecipient}
        onUpdate={handleUnsavedRecipientUpdate}
      />

      <PostingDialog
        isOpen={postingDialogIsOpen}
        handleClose={togglePostingDialog}
        form={form}
      />
    </div>
  );
};

export default BulkPaymentTable;
